import clsx from 'clsx'
import {FC, type Ref, memo, useId, useMemo, useRef} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Flatpickr, {DateTimePickerHandle, DateTimePickerProps} from 'react-flatpickr'
import {useIntl} from 'react-intl'

import {WithChildren} from '_metronic/helpers'

type PersistentFlatpickrProps = DateTimePickerProps & {
  pickerRef: Ref<DateTimePickerHandle>
  keepInstance: boolean
}

const PersistentFlatpickr = memo(
  function PersistentFlatpickrComponent({
    pickerRef,
    keepInstance,
    ...pickerProps
  }: PersistentFlatpickrProps) {
    void keepInstance
    return <Flatpickr ref={pickerRef} {...pickerProps} />
  },
  (_previousProps, nextProps) => nextProps.keepInstance
)

export type DatePickerProps = {
  label?: string
  isClearable?: boolean
  onClear?: () => void
  groupClass?: string
  helperText?: string
  closeOnSelect?: boolean
} & DateTimePickerProps &
  WithChildren

export const DatePicker: FC<DatePickerProps> = ({
  label,
  isClearable,
  onClear,
  onChange,
  onOpen,
  onClose,
  placeholder,
  groupClass,
  options,
  title,
  helperText,
  closeOnSelect = true,
  children,
  ...props
}) => {
  const intl = useIntl()
  const id = useId()
  const fpRef = useRef<DateTimePickerHandle>(null)
  const isOpenRef = useRef(false)

  const labelHasTranslation = label ? !!intl.messages[label] : false
  const titleHasTranslation = title ? !!intl.messages[title] : false

  const renderTooltip = useMemo(() => {
    return (
      <Tooltip id={`tooltip-${id}`}>
        {titleHasTranslation ? intl.formatMessage({id: title}) : title}
      </Tooltip>
    )
  }, [title, id, intl, titleHasTranslation])

  let hasValue = false
  if (props.value) {
    if (Array.isArray(props.value)) {
      hasValue = props.value.some((value) => !!value)
    } else {
      hasValue = !!props.value
    }
  }

  const flatpickrOptions = useMemo(() => {
    return {
      dateFormat: 'd-m-Y',
      // If time is enabled, we usually want closeOnSelect to be false
      // so the user can finish picking minutes/seconds.
      closeOnSelect: options?.enableTime ? false : closeOnSelect,
      ...options,
    }
  }, [options, closeOnSelect])

  return (
    <div className={clsx(groupClass)}>
      {label && (
        <label className={clsx('form-label', {required: props.required})} htmlFor={id}>
          {labelHasTranslation ? intl.formatMessage({id: label}) : label}
          {title && (
            <OverlayTrigger placement='right' overlay={renderTooltip}>
              <div className='badge pe-auto'>
                <i className='bi bi-info-circle-fill'></i>
              </div>
            </OverlayTrigger>
          )}
        </label>
      )}

      <div className='position-relative'>
        <PersistentFlatpickr
          pickerRef={fpRef}
          keepInstance={options?.enableTime === true && isOpenRef.current}
          className='form-control me-9'
          placeholder={
            placeholder ||
            (options?.noCalendar && options?.enableTime
              ? intl.$t({id: 'GENERAL.SELECT_TIME'})
              : intl.$t({id: 'GENERAL.SELECT_DATE'}))
          }
          options={flatpickrOptions}
          onChange={(...changeOptions) => {
            if (typeof onChange === 'function') {
              onChange(...changeOptions)
            }
          }}
          onOpen={(...openOptions) => {
            isOpenRef.current = true
            if (typeof onOpen === 'function') onOpen(...openOptions)
          }}
          onClose={(...closeOptions) => {
            isOpenRef.current = false
            if (typeof onClose === 'function') onClose(...closeOptions)
          }}
          {...props}
        />
        {isClearable && hasValue && props.value && (
          <button
            type='button'
            className='btn btn-icon btn-sm btn-light-danger rounded-circle h-25px w-25px me-2 position-absolute p-1 end-0 top-50 translate-middle-y'
            onClick={() => {
              if (onClear) onClear()
            }}
          >
            <i className='fa fa-xmark' />
          </button>
        )}
      </div>

      {helperText && (
        <small id={`${id}-help-text`} className='form-text text-muted'>
          {helperText}
        </small>
      )}

      {children}
    </div>
  )
}
